import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
  type PaletteMode,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { DatasetViewerSettingsModal } from "views/DatasetViewer/DatasetViewerSettingsModal";
import axiosInstance from "utils/request";

interface NavBarTitleContextValue {
  title: string;
  setTitle: (title: string) => void;
  mode: PaletteMode;
  setMode: (mode: PaletteMode) => void;
}

const NavBarTitleContext = createContext<NavBarTitleContextValue | null>(null);

export function NavBarTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<PaletteMode>("light");
  const value = useMemo(
    () => ({ title, setTitle, mode, setMode }),
    [title, mode]
  );
  return (
    <NavBarTitleContext.Provider value={value}>
      {children}
    </NavBarTitleContext.Provider>
  );
}

export function useNavBarTitle(title: string) {
  const ctx = useContext(NavBarTitleContext);
  if (!ctx) throw new Error("useNavBarTitle must be used inside NavBarTitleProvider");
  const { setTitle } = ctx;
  useEffect(() => {
    setTitle(title);
    return () => setTitle("");
  }, [title, setTitle]);
}

export function useNavBarMode(mode: PaletteMode) {
  const ctx = useContext(NavBarTitleContext);
  if (!ctx) throw new Error("useNavBarMode must be used inside NavBarTitleProvider");
  const { setMode } = ctx;
  useEffect(() => {
    setMode(mode);
    return () => setMode("light");
  }, [mode, setMode]);
}

export function NavBar() {
  const navigate = useNavigate();
  const ctx = useContext(NavBarTitleContext);
  const title = ctx?.title ?? "";
  const mode = ctx?.mode ?? "light";

  async function handleLogout() {
    try {
      await axiosInstance.post("/auth/logout");
    } catch {
      // session may already be gone
    }
    navigate("/login");
  }

  const dark = mode === "dark";

  return (
    <AppBar
      position="static"
      color={dark ? "transparent" : "default"}
      elevation={1}
      data-testid="nav-bar"
      data-mode={mode}
      sx={
        dark
          ? {
              bgcolor: "#2d2d2d",
              color: "common.white",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
            }
          : undefined
      }
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="home"
          onClick={() => navigate("/")}
          data-testid="nav-home"
        >
          <HomeIcon />
        </IconButton>
        {title && (
          <Typography
            variant="h6"
            data-testid="nav-title"
            sx={{ ml: 1, lineHeight: "24px" }}
          >
            {title}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <DatasetViewerSettingsModal />
        <Tooltip title="Logout">
          <IconButton
            color="inherit"
            onClick={handleLogout}
            data-testid="nav-logout"
            aria-label="Logout"
            sx={{ ml: 1 }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
}
